import * as signalR from '@microsoft/signalr';
import { useAuthStore } from '../store/authStore';

// Use same fallback pattern as API service
// When VITE_API_URL is not set, use '/api/v1' which Vite will proxy to backend
const API_URL = import.meta.env.VITE_API_URL || '/api/v1';
const BASE_URL = API_URL.replace(/\/api(\/v\d+)?/, '');  // Remove /api or /api/v1 suffix for hub URLs

/** Generic hub event callback. Payloads are typed by the caller via generics. */
export type WebSocketEventCallback = (...args: unknown[]) => void;

/**
 * 'fastsync' is not a group but a per-screen request that must survive a
 * reconnect exactly like a group membership does (the player only streams
 * AudienceTick/VisionEvent while some viewer holds fast-sync on it).
 */
type SubscriptionKind = 'screen' | 'campaign' | 'bookings' | 'fastsync';

/**
 * App-lifetime SignalR connection to the PlaybackHub.
 *
 * Design rules (learned from real production failures — keep them):
 *
 * 1. ONE connection for the whole app. Components NEVER stop it — a component
 *    unmounting used to call disconnect() on this shared singleton, killing
 *    real-time updates for every other page and racing concurrent connect()
 *    calls into "Failed to start the HttpConnection before stop() was called".
 *    Only shutdown() (logout) stops the connection.
 *
 * 2. Listener registrations survive reconnects AND new connection objects.
 *    signalR keeps .on() registrations across automatic reconnects of the same
 *    connection object, but a fresh connect() builds a NEW object — every event
 *    name in the registry is re-bound to it.
 *
 * 3. Group memberships are re-established after every reconnect. SignalR
 *    groups are per-connection-id server-side, so an automatic reconnect
 *    silently drops them — without re-subscribing, pages looked "connected"
 *    but never received another event.
 *
 * 4. Screen/campaign groups and fast-sync requests are reference-counted.
 *    Several components share one screen room (the Audience tab's tick hook,
 *    the Live Activity tab, the vision feed…); the group is only left — and
 *    the player only told to slow back down — when the LAST consumer goes.
 */
class WebSocketService {
    private connection: signalR.HubConnection | null = null;
    private connectionState: 'disconnected' | 'connecting' | 'connected' | 'reconnecting' = 'disconnected';
    private listeners: Map<string, Set<WebSocketEventCallback>> = new Map();
    /** Event names already bound to the CURRENT connection object. */
    private boundEvents: Set<string> = new Set();
    /** Server-side group subscriptions (and fast-sync requests) to restore after reconnects. */
    private subscriptions: Map<string, { kind: SubscriptionKind; id: string }> = new Map();
    /** Consumers per subscription key — the server is only told to leave at zero. */
    private refs: Map<string, number> = new Map();
    private connectionPromise: Promise<void> | null = null;
    private shuttingDown = false;

    async connect(): Promise<void> {
        this.shuttingDown = false;

        if (this.connectionState === 'connected' && this.connection?.state === signalR.HubConnectionState.Connected) {
            return;
        }

        if (this.connectionPromise) {
            return this.connectionPromise;
        }

        this.connectionState = 'connecting';

        const url = `${BASE_URL}/hubs/playback`;

        const connection = new signalR.HubConnectionBuilder()
            .withUrl(url, {
                transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
                skipNegotiation: false,
                // Server-side SubscribeToScreen/SubscribeToCampaign check
                // screen/campaign ownership against the caller's JWT identity,
                // so the dashboard connection needs to present it.
                accessTokenFactory: () => useAuthStore.getState().accessToken || '',
            })
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: (retryContext) => {
                    // Exponential backoff capped at 30s, retrying indefinitely —
                    // a dashboard left open overnight must recover on its own.
                    return Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 30000);
                }
            })
            .configureLogging(signalR.LogLevel.Warning)
            .build();

        connection.onreconnecting(() => {
            this.connectionState = 'reconnecting';
            console.log('[WebSocket] Reconnecting...');
        });

        connection.onreconnected(async () => {
            this.connectionState = 'connected';
            console.log('[WebSocket] Reconnected — restoring group subscriptions');
            await this.restoreSubscriptions();
        });

        connection.onclose((error) => {
            this.connectionState = 'disconnected';
            this.connectionPromise = null;
            if (error) {
                console.error('[WebSocket] Connection closed with error:', error);
            }
        });

        this.connection = connection;
        this.boundEvents.clear();
        // Bind every already-registered event name to the new connection object.
        for (const eventName of this.listeners.keys()) {
            this.bindEvent(eventName);
        }

        this.connectionPromise = (async () => {
            try {
                await connection.start();
                this.connectionState = 'connected';
                console.log('✓ [WebSocket] Connected to PlaybackHub');
                await this.restoreSubscriptions();
            } catch (error) {
                this.connectionState = 'disconnected';
                this.connectionPromise = null;
                this.connection = null;
                if (!this.shuttingDown) {
                    console.error('✗ [WebSocket] Connection failed:', error);
                }
                throw error;
            }
        })();

        return this.connectionPromise;
    }

    /**
     * Fully stop the connection and forget all subscriptions. ONLY for logout —
     * components must never call this (see class doc).
     */
    async shutdown() {
        this.shuttingDown = true;
        this.subscriptions.clear();
        this.refs.clear();
        const connection = this.connection;
        this.connection = null;
        this.connectionPromise = null;
        this.boundEvents.clear();
        this.connectionState = 'disconnected';
        if (connection) {
            try {
                await connection.stop();
            } catch {
                // Best-effort — the token may already be gone.
            }
            console.log('[WebSocket] Shut down');
        }
    }

    /** @deprecated Components must not stop the shared connection; use shutdown() from logout only. */
    async disconnect() {
        await this.shutdown();
    }

    getConnectionState() {
        return this.connectionState;
    }

    isConnected() {
        return this.connection?.state === signalR.HubConnectionState.Connected;
    }

    // ── Group subscriptions (tracked + refcounted + auto-restored) ────────────

    /**
     * Hub groups are named from the raw id string (`screen_{id}`) while the
     * server broadcasts to the Guid's canonical lowercase form — so ids are
     * lowercased here, and the refcount key is the same normalised id.
     */
    private track(kind: SubscriptionKind, rawId: string): { id: string } {
        const id = rawId.toLowerCase();
        const key = `${kind}:${id}`;
        this.refs.set(key, (this.refs.get(key) ?? 0) + 1);
        this.subscriptions.set(key, { kind, id });
        return { id };
    }

    /** Drop one consumer; true when that was the last one (server should be told). */
    private release(kind: SubscriptionKind, rawId: string): { id: string; last: boolean } {
        const id = rawId.toLowerCase();
        const key = `${kind}:${id}`;
        const n = (this.refs.get(key) ?? 1) - 1;
        if (n > 0) {
            this.refs.set(key, n);
            return { id, last: false };
        }
        this.refs.delete(key);
        this.subscriptions.delete(key);
        return { id, last: true };
    }

    async subscribeToScreen<TData = unknown>(screenId: string, callback?: (data: TData) => void) {
        if (callback) {
            this.on('OnContentPlaying', callback);
        }
        const { id } = this.track('screen', screenId);
        // Re-invoking for an already-joined group is idempotent server-side and
        // covers a subscription made while a reconnect was in flight.
        await this.invoke('SubscribeToScreen', id);
        console.log(`[WebSocket] Subscribed to screen ${id}`);
    }

    async unsubscribeFromScreen(screenId: string) {
        const { id, last } = this.release('screen', screenId);
        if (!last) return;
        await this.invokeIfConnected('UnsubscribeFromScreen', id);
    }

    async subscribeToCampaign<TData = unknown>(campaignId: string, callback?: (data: TData) => void) {
        if (callback) {
            this.on('OnContentPlaying', callback);
        }
        const { id } = this.track('campaign', campaignId);
        await this.invoke('SubscribeToCampaign', id);
        console.log(`[WebSocket] Subscribed to campaign ${id}`);
    }

    async unsubscribeFromCampaign(campaignId: string) {
        const { id, last } = this.release('campaign', campaignId);
        if (!last) return;
        await this.invokeIfConnected('UnsubscribeFromCampaign', id);
    }

    /** Join the per-user booking events group (BookingCreated/Approved/...). */
    async subscribeToBookings(userId: string) {
        this.subscriptions.set(`bookings:${userId}`, { kind: 'bookings', id: userId });
        await this.invoke('SubscribeToBookings', userId);
        console.log(`[WebSocket] Subscribed to booking events for user ${userId}`);
    }

    async unsubscribeFromBookings(userId: string) {
        this.subscriptions.delete(`bookings:${userId}`);
        await this.invokeIfConnected('UnsubscribeFromBookings', userId);
    }

    private async restoreSubscriptions() {
        // Groups first, then fast-sync: the player's SetSyncMode rides the
        // screen group, so re-join before re-requesting.
        const subs = [...this.subscriptions.values()]
            .sort((a, b) => Number(a.kind === 'fastsync') - Number(b.kind === 'fastsync'));
        for (const sub of subs) {
            try {
                if (sub.kind === 'screen') {
                    await this.connection!.invoke('SubscribeToScreen', sub.id);
                } else if (sub.kind === 'campaign') {
                    await this.connection!.invoke('SubscribeToCampaign', sub.id);
                } else if (sub.kind === 'fastsync') {
                    await this.connection!.invoke('RequestFastSync', sub.id);
                } else {
                    await this.connection!.invoke('SubscribeToBookings', sub.id);
                }
            } catch (error) {
                console.warn(`[WebSocket] Failed to restore ${sub.kind} subscription ${sub.id}:`, error);
            }
        }
    }

    // ── Sync-mode requests (refcounted + auto-restored) ───────────────────────

    /**
     * Ask the player to sync fast and stream live vision data — call while a
     * live surface for the screen is mounted. Connect-first (a request made
     * before the hub is up used to be silently dropped), recorded so it is
     * re-sent after a reconnect, and refcounted so one surface unmounting
     * does not slow the player down for another still watching.
     */
    async requestFastSync(screenId: string) {
        const { id } = this.track('fastsync', screenId);
        await this.invoke('RequestFastSync', id);
    }

    /**
     * Re-send an existing hold without touching the refcount. The hub relays
     * "fast" to the player on every request, so a live surface calling this
     * every 30 s heals a SetSyncMode the player never heard (sent while its
     * hub connection was being rebuilt) instead of staying silent until the
     * viewer leaves and comes back.
     */
    async reassertFastSync(screenId: string) {
        await this.invokeIfConnected('RequestFastSync', screenId);
    }

    /** Release one fast-sync consumer; the player is told 'normal' only by the last one. */
    async requestNormalSync(screenId: string) {
        const { id, last } = this.release('fastsync', screenId);
        if (!last) return;
        await this.invokeIfConnected('RequestNormalSync', id);
    }

    // ── Event listeners ───────────────────────────────────────────────────────

    private bindEvent(eventName: string) {
        if (!this.connection || this.boundEvents.has(eventName)) return;
        this.boundEvents.add(eventName);
        this.connection.on(eventName, (...args: unknown[]) => {
            const callbacks = this.listeners.get(eventName);
            if (callbacks) {
                callbacks.forEach(cb => cb(...args));
            }
        });
    }

    on<TArgs extends unknown[] = unknown[]>(eventName: string, callback: (...args: TArgs) => void) {
        if (!this.listeners.has(eventName)) {
            this.listeners.set(eventName, new Set());
        }
        this.listeners.get(eventName)!.add(callback as WebSocketEventCallback);
        // Bind lazily: safe to call before connect() — connect() re-binds every
        // registered event onto each new connection object.
        this.bindEvent(eventName);
    }

    off<TArgs extends unknown[] = unknown[]>(eventName: string, callback: (...args: TArgs) => void) {
        const callbacks = this.listeners.get(eventName);
        if (callbacks) {
            callbacks.delete(callback as WebSocketEventCallback);
        }
    }

    // ── Raw invocations ───────────────────────────────────────────────────────

    async invoke<TResult = unknown>(methodName: string, ...args: unknown[]): Promise<TResult> {
        if (!this.isConnected()) {
            try {
                await this.connect();
            } catch (error) {
                console.warn(`[WebSocket] Failed to connect before invoking ${methodName}:`, error);
                throw new Error(`Cannot invoke ${methodName}: WebSocket connection failed`);
            }
        }

        if (!this.isConnected()) {
            throw new Error(`Cannot invoke ${methodName}: WebSocket not connected`);
        }

        try {
            return await this.connection!.invoke<TResult>(methodName, ...args);
        } catch (error) {
            console.error(`[WebSocket] Error invoking ${methodName}:`, error);
            throw error;
        }
    }

    // Safe invoke that doesn't throw if not connected - useful for cleanup
    async invokeIfConnected<TResult = unknown>(methodName: string, ...args: unknown[]): Promise<TResult | undefined> {
        if (!this.isConnected()) {
            return;
        }

        try {
            return await this.connection!.invoke<TResult>(methodName, ...args);
        } catch (error) {
            console.warn(`[WebSocket] Error invoking ${methodName}:`, error);
            // Don't throw - this is a "best effort" call
        }
    }
}

// Export singleton instance
export const websocketService = new WebSocketService();
